import {
	OfferTemplate,
	type CommodityGroup,
	type RuleError,
	type ShopItemAttributes,
	type Slot,
	type SlotItem,
} from "../../types/shop";
import type { GroupRule, TemplateRules } from "./rules";
import { ITEM_COUNT_LIMITS, OFFER_TEMPLATE_RULES } from "./rules";

const push = (
	errors: RuleError[],
	path: string,
	code: string,
	message: string,
): number => errors.push({ path, code, message });

// Checks whether all items in the slot belong to the same commodity group.
const getSingleGroup = (items: SlotItem[]): CommodityGroup | null => {
	if (!items?.length) return null;
	const g = items[0].group;
	for (const it of items) if (it.group !== g) return null;
	return g;
};

const validateSlot = (
	slot: Slot,
	rule: GroupRule,
	path: string,
): RuleError[] => {
	const errors: RuleError[] = [];

	if (!slot?.items?.length) {
		push(errors, `${path}.items`, "EMPTY", "Slot must contain items.");
		return errors;
	}

	// Text rule (works for both single + mix)
	if (slot.text?.trim()) {
		const onlyOne = slot.items.length === 1;
		const g0 = slot.items[0]?.group;
		const allowText = rule.allowTextWhenSingle?.has(g0) ?? false;

		if (!onlyOne || !allowText) {
			push(
				errors,
				`${path}.text`,
				"TEXT_NOT_ALLOWED",
				"Text is allowed only when the slot contains exactly 1 building or 1 upgrade.",
			);
		}
	}

	if (rule.mode === "single") {
		const group = getSingleGroup(slot.items);
		if (!group) {
			push(
				errors,
				`${path}.items`,
				"MIXED_GROUPS",
				"Slot must contain items from exactly one commodity group.",
			);
			return errors;
		}

		if (!rule.allowed.has(group)) {
			push(
				errors,
				`${path}.items`,
				"GROUP_NOT_ALLOWED",
				`Group '${group}' is not allowed in this slot.`,
			);
			return errors;
		}

		const n = slot.items.length;

		// 1. Get base limits from perGroupLimits or fallback to global ITEM_COUNT_LIMITS
		const base = rule.perGroupLimits?.[group] ?? ITEM_COUNT_LIMITS[group];

		// 2. Resolve final min/max using rule overrides or base limits
		const min = base.min;
		const max = base.max;

		if (n < min || n > max) {
			push(
				errors,
				`${path}.items`,
				"COUNT_OUT_OF_RANGE",
				`Item count for '${group}' must be ${min}–${max}.`,
			);
		}

		return errors;
	}

	// mode === "mix"
	const countsByGroup = slot.items.reduce<Record<CommodityGroup, number>>(
		(
			acc: Record<CommodityGroup, number>,
			it: SlotItem,
		): Record<CommodityGroup, number> => {
			acc[it.group] = (acc[it.group] ?? 0) + 1;
			return acc;
		},
		{} as Record<CommodityGroup, number>,
	);

	// Validate allowed groups
	for (const g of Object.keys(countsByGroup) as CommodityGroup[]) {
		if (!rule.allowed.has(g)) {
			push(
				errors,
				`${path}.items`,
				"GROUP_NOT_ALLOWED",
				`Group '${g}' is not allowed in this slot.`,
			);
		}
	}

	// Validate per-group limits
	for (const [g, n] of Object.entries(countsByGroup) as Array<
		[CommodityGroup, number]
	>) {
		const base = ITEM_COUNT_LIMITS[g];
		const local = rule.perGroupLimits?.[g] ?? base;

		const min = Math.max(local.min, base.min);
		const max = Math.min(local.max, base.max);

		if (n < min || n > max) {
			push(
				errors,
				`${path}.items`,
				"COUNT_OUT_OF_RANGE",
				`Item count for '${g}' in this slot must be ${min}–${max}.`,
			);
		}
	}

	return errors;
};

// validateTransitions checks only the group order between slots (validateSlot checks slot-level allowance).
// With current rules, this applies only to SLOTS_3 and SLOTS_4 templates.
const validateTransitions = (
	groups: CommodityGroup[],
	rules: TemplateRules,
	basePath: string,
): RuleError[] => {
	const errors: RuleError[] = [];
	if (!rules.allowedStart || !rules.transitions) return errors;

	if (!rules.allowedStart.has(groups[0])) {
		push(
			errors,
			`${basePath}slots[0].items`,
			"START_GROUP_NOT_ALLOWED",
			"Slot1 group is not allowed.",
		);
	}

	for (let i = 1; i < groups.length; i++) {
		const prev = groups[i - 1];
		const cur = groups[i];
		const allowed = rules.transitions.get(prev);
		if (!allowed?.has(cur)) {
			push(
				errors,
				`${basePath}slots[${i}].items`,
				"INVALID_TRANSITION",
				`Invalid transition '${prev}' -> '${cur}'.`,
			);
		}
	}

	return errors;
};

export const validateOfferAttributes = (a: ShopItemAttributes): RuleError[] => {
	const rules = OFFER_TEMPLATE_RULES[a.template];

	if (rules === undefined)
		return [
			{
				path: "template",
				code: "UNKNOWN_TEMPLATE",
				message: "Unknown template.",
			},
		];

	// Event passes don't have rules, it's a special case
	if (rules === null) {
		return [];
	}

	const errors: RuleError[] = [];
	const expectedSlots = rules.slots.length;

	if ("slots" in a && Array.isArray(a.slots)) {
		if (a.slots.length !== expectedSlots) {
			push(
				errors,
				"slots",
				"SLOT_COUNT_MISMATCH",
				`Expected ${expectedSlots} slots for template '${a.template}'.`,
			);
			return errors;
		}

		const groups: CommodityGroup[] = [];

		// Validate slots one by one
		for (let i = 0; i < expectedSlots; i++) {
			const slotPath = `slots[${i}]`;
			errors.push(...validateSlot(a.slots[i], rules.slots[i], slotPath));

			const g =
				rules.slots[i].mode === "single"
					? getSingleGroup(a.slots[i].items)
					: null;

			if (g) groups.push(g);
		}

		// Validate transitions only when every slot has exactly one commodity group.
		// If any slot mixes groups (mode: "mix"), we skip transitions because the order is ambiguous.
		if (groups.length === expectedSlots) {
			errors.push(...validateTransitions(groups, rules, ""));
		}
	}

	if (a.template === OfferTemplate.PIGGY_BANK) {
		if (!a?.activity) {
			push(
				errors,
				"meta.piggy",
				"PIGGY_ACTIVITY_REQUIRED",
				"Piggy bank requires activity QuestId.",
			);
		}
	}

	return errors;
};
